
/**
 * Shared autosize text renderer.
 *
 * ใช้ร่วมกันระหว่าง useRenderText (V1/V3/Editor) และ useRenderSingleFile
 * เพื่อไม่ให้ logic autosize แตกออกเป็นสองก๊อปปี้อีก
 */

let _segmenter: any = null

function getSegmenter() {
  if (_segmenter !== null) {
    return _segmenter
  }
  try {
    _segmenter = (typeof Intl !== 'undefined' && (Intl as any).Segmenter)
      ? new (Intl as any).Segmenter('th', { granularity: 'grapheme' })
      : false
  } catch (error) {
    _segmenter = false
  }
  return _segmenter
}

function splitGraphemes(text: string): string[] {
  const segmenter = getSegmenter()
  if (!segmenter) {
    return Array.from(text)
  }
  return [...segmenter.segment(text)].map((s: any) => s.segment)
}

/**
 * canvas จะ "เมิน" ค่า ctx.font ทั้งบรรทัดถ้าชื่อฟอนต์ไม่ใช่ CSS ที่ถูกต้อง
 * เราจะใส่ quote ให้เองตอน setFont จึงต้องตัด quote ที่อาจติดมากับ template ออกก่อน
 */
function normalizeFontFamily(fontFamily: any): string {
  if (!fontFamily) {
    return ''
  }
  return String(fontFamily).trim().replace(/^['"]+|['"]+$/g, '').trim()
}

export const useTextFit = () => {
  return {
    drawTextToFitArea,
  }
}

function drawTextToFitArea(ctx: any, _words: any, maxWidth: number, maxHeight: number, options: any = {}) {
  const minFontSize = options.minFontSize || 10
  const maxFontSize = options.maxFontSize || 100
  const verticalAlign = options.verticalAlign || 'top'
  const horizontalAlign = options.horizontalAlign || 'left'
  const fontFamily = normalizeFontFamily(options.fontFamily) || 'Arial'
  const fontWeight = options.fontWeight || 'normal'
  const lineHeightMultiplier = options.lineHeight || 1.5
  const fill = options.fill || 'black'
  const letterSpacing = options.letterSpacing || 0

  const words: any[] = Array.isArray(_words) ? _words : []

  //color fill
  ctx.fillStyle = fill
  // การวัด ink ทั้งหมดข้างล่างอิง baseline "top" จึงต้องตั้งก่อนวัด
  ctx.textBaseline = 'top'

  if (!words.length) {
    return minFontSize
  }

  // ความกว้างของ grapheme ใช้ได้เฉพาะกับฟอนต์ที่ตั้งอยู่บน ctx ตอนนั้น
  let graphemeWidthCache = new Map<string, number>()

  function setFont(fontSize: number) {
    ctx.font = `${fontWeight} ${fontSize}px "${fontFamily}"`
    graphemeWidthCache = new Map<string, number>()
  }

  function graphemeWidth(grapheme: string) {
    let width = graphemeWidthCache.get(grapheme)
    if (width === undefined) {
      width = ctx.measureText(grapheme).width
      graphemeWidthCache.set(grapheme, width as number)
    }
    return width as number
  }

  // 🔹 helper: measure text with letter spacing
  function measureTextWithSpacing(text: string) {
    if (letterSpacing === 0) {
      return ctx.measureText(text).width
    }

    const graphemes = splitGraphemes(text)
    let width = 0

    graphemes.forEach((g, i) => {
      width += graphemeWidth(g)
      if (i < graphemes.length - 1) {
        width += letterSpacing
      }
    })

    return width
  }

  // 🔹 helper: draw text with letter spacing
  function fillTextWithSpacing(text: string, x: number, y: number) {
    if (letterSpacing === 0) {
      ctx.fillText(text, x, y)
      return
    }

    let currentX = x
    const graphemes = splitGraphemes(text)

    for (let i = 0; i < graphemes.length; i++) {
      const char = graphemes[i]
      ctx.fillText(char, currentX, y)
      currentX += graphemeWidth(char) + letterSpacing
    }
  }

  // ตัดบรรทัดด้วยฟอนต์ที่ตั้งอยู่บน ctx ตอนนั้น
  // ใช้ฟังก์ชันเดียวกันนี้ทั้งตอนเช็ค fit และตอนวาดจริง
  function wrapLines() {
    const lines: string[] = []
    let currentLine = words[0]

    for (let i = 1; i < words.length; i++) {
      const word = words[i]
      if (measureTextWithSpacing(currentLine + word) <= maxWidth) {
        currentLine += word
      } else {
        lines.push(currentLine)
        currentLine = word
      }
    }
    lines.push(currentLine)

    return lines
  }

  // ความสูงของบล็อกข้อความ วัดจากขอบ glyph จริง (รวมวรรณยุกต์/สระบน-ล่างของไทย)
  // แทนการนับ line-height เต็มช่องให้บรรทัดสุดท้าย ซึ่งประเมินสูงเกินจริง
  function layoutAt(fontSize: number) {
    setFont(fontSize)
    const lines = wrapLines()
    const lineHeight = fontSize * lineHeightMultiplier

    let inkTop = 0
    let inkBottom = fontSize * lineHeightMultiplier

    const first = ctx.measureText(lines[0])
    const last = ctx.measureText(lines[lines.length - 1])
    if (typeof first.actualBoundingBoxAscent === 'number') {
      // ด้วย textBaseline "top" ค่า ascent > 0 แปลว่า glyph ล้นขึ้นเหนือ y ของบรรทัด
      inkTop = Math.max(0, first.actualBoundingBoxAscent)
      inkBottom = Math.max(fontSize, last.actualBoundingBoxDescent)
    }

    const height = inkTop + (lines.length - 1) * lineHeight + inkBottom

    return { lines, lineHeight, inkTop, height }
  }

  function fits(fontSize: number) {
    const { lines, height } = layoutAt(fontSize)
    return height <= maxHeight && lines.every(line => measureTextWithSpacing(line) <= maxWidth)
  }

  // Binary search หาขนาดใหญ่สุดที่ยังอยู่ในกรอบ
  let optimalFontSize = minFontSize
  let low = minFontSize
  let high = maxFontSize
  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    if (fits(mid)) {
      optimalFontSize = mid
      low = mid + 1
    } else {
      high = mid - 1
    }
  }

  // จัด layout ใหม่ที่ขนาดที่เลือกจริง เพราะรอบสุดท้ายของ binary search
  // อาจเป็นขนาดอื่นที่ไม่ผ่าน ถ้าเอา lines ชุดนั้นมาวาดจะได้บรรทัดเกินแล้วล้นกรอบ
  const { lines, lineHeight, inkTop, height } = layoutAt(optimalFontSize)

  let y
  if (verticalAlign === 'middle') {
    y = Math.max((maxHeight - height) / 2, 0)
  } else if (verticalAlign === 'bottom') {
    y = Math.max(maxHeight - height, 0)
  } else {
    y = 0
  }
  y += inkTop

  // 🔹 draw lines
  lines.forEach((line) => {
    let x
    const lineWidth = measureTextWithSpacing(line)

    if (horizontalAlign === 'center') {
      x = (maxWidth - lineWidth) / 2
    } else if (horizontalAlign === 'right') {
      x = maxWidth - lineWidth
    } else {
      x = 0
    }

    fillTextWithSpacing(line, x, y)
    y += lineHeight
  })

  return optimalFontSize
}
